import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

const TRANSFORM_PATHS_0 = [
  { path: 'identity.createdDate' },
  { path: 'identity.updatedDate' },
  { path: 'identity.identityProfile.customFields.value.dateValue' },
  { path: 'additionalData.*.dateValue' },
];
const TRANSFORM_PATHS_1 = [
  { path: 'identity.identityProfile.customFields.value.numValue' },
  { path: 'additionalData.*.numValue' },
];

function resolveWixIamVerificationV1VerificationServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www.wixapis.com': [
      {
        srcPath: '/_api/iam/verification',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/iam/verification',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/iam/verification',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/iam/verification/v1/auth/resend',
        destPath: '/v1/auth/resend',
      },
      {
        srcPath: '/iam/verification/v1/Start',
        destPath: '/v1/Start',
      },
      {
        srcPath: '/iam/verification/v1/auth/verify',
        destPath: '/v1/auth/verify',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_identity_verification';

/**
 * Starts an email verification process by sending a verification code to the identity's email address.
 * The code is sent to the email associated with the provided `identityId`, and the returned `verificationId`
 * identifies the verification process.
 *
 * Email verification is required when the registering member is already listed as a contact, or when it
 * is required by the site's member settings.
 *
 * During registration or authentication, the member then completes verification by calling
 * `VerifyDuringAuthentication` with the `state_token` of the `REQUIRE_EMAIL_VERIFICATION` state — not the
 * `verificationId` returned here, which `VerifyDuringAuthentication` does not accept.
 */
export function start(payload: object): RequestOptionsFactory<any> {
  function __start({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.iam.verification.v1.start_response',
      method: 'POST' as any,
      methodFqn: 'wix.iam.verification.v1.VerificationService.Start',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIamVerificationV1VerificationServiceUrl({
        protoPath: '/v1/start',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __start;
}

/**
 * Continues the registration process when a member is required to verify an email address
 * using a verification code received by email.
 *
 * Email verification is required when the registering member is already listed as a contact.
 *
 * Typically, after a successful verification, you generate and use member tokens for the
 * registered member so that subsequent API calls are called as part of a member session.
 */
export function verifyDuringAuthentication(
  payload: object
): RequestOptionsFactory<any> {
  function __verifyDuringAuthentication({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.iam.verification.v1.start_response',
      method: 'POST' as any,
      methodFqn:
        'wix.iam.verification.v1.VerificationService.VerifyDuringAuthentication',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIamVerificationV1VerificationServiceUrl({
        protoPath: '/v1/auth/verify',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: TRANSFORM_PATHS_0,
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: TRANSFORM_PATHS_1,
          },
        ]),
    };

    return metadata;
  }

  return __verifyDuringAuthentication;
}
